import { useCallback, useEffect, useRef, useState } from "react";
import { api, type Etapa, type Totalizadores } from "../lib/api";
import { CardTopo } from "../components/CardTopo";
import { CardEtapa } from "../components/CardEtapa";
import { FiltrosBar } from "../components/FiltrosBar";
import { BuscaPcf } from "../components/BuscaPcf";
import { useNow } from "../hooks/useNow";
import { useTheme } from "../hooks/useTheme";
import { Logo } from "../components/Logo";
import { logout, type Usuario } from "../lib/auth";
import { MsgModal } from "../components/MsgModal";

interface DashboardProps {
  onAbrirEtapa: (etapa: Etapa, cliente: string) => void;
  onAbrirApontamento: () => void;
  onAbrirPlanejamento: () => void;
  onAbrirProducao: (tela: string) => void; // telas do menu PRODUCAO (fila, finalização, ...)
  // painel_producao L1545-1587: cards e buscas abrem a consulta/planejamento com o cliente do filtro.
  onAbrirConsulta: (parametro: string, cliente: string, numpcf?: number, numped?: number) => void;
  onAbrirPedidos: (cliente: string) => void; // card 51 -> callpedido
  onAbrirNaoProgramadas: (cliente: string) => void; // card 52 -> atcadpcf NAO_PROGRAMADAS
  usuario: Usuario;
}

export function Dashboard({
  onAbrirEtapa,
  onAbrirApontamento,
  onAbrirPlanejamento,
  onAbrirProducao,
  onAbrirConsulta,
  onAbrirPedidos,
  onAbrirNaoProgramadas,
  usuario,
}: DashboardProps) {
  const [clientes, setClientes] = useState<string[]>([]);
  const [cliente, setCliente] = useState<string>("TODOS");
  const [refreshSec, setRefreshSec] = useState<number>(30);
  const [totais, setTotais] = useState<Totalizadores | null>(null);
  const [etapas, setEtapas] = useState<Etapa[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const now = useNow();
  const { theme, toggle } = useTheme();

  const clienteParam = cliente === "TODOS" ? null : cliente;

  const carregar = useCallback(async () => {
    try {
      setErro(null);
      const [t, e] = await Promise.all([
        api.totalizadores(clienteParam),
        api.etapas(clienteParam),
      ]);
      setTotais(t);
      setEtapas(e);
    } catch (err) {
      setErro((err as Error).message);
    }
  }, [clienteParam]);

  useEffect(() => {
    api
      .clientes()
      .then(setClientes)
      .catch((err) => setErro(String(err)));
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    if (refreshSec > 0) {
      timerRef.current = window.setInterval(() => void carregar(), refreshSec * 1000);
    }
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [refreshSec, carregar]);

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <Logo />
          <h1>
            Painel de Controle - Produção
            <small>Tempo real</small>
          </h1>
        </div>
        <span className="spacer" />
        <span className="clock">{now}</span>
        <div className="user-badge" title={`Usuário: ${usuario.nomusu}`}>
          <span className="user-avatar">{usuario.apelid.charAt(0).toUpperCase()}</span>
          <span className="user-nome">{usuario.nomusu.split(" ")[0]}</span>
        </div>
        <button className="btn-secundario" onClick={onAbrirPlanejamento} title="PCP — Planejamento e Controle de Produção">
          🗓️ PCP
        </button>
        <button className="btn-secundario" onClick={onAbrirApontamento} title="Apontamento de chão de fábrica">
          Apontamento
        </button>
        <select
          className="btn-secundario sel-producao"
          value=""
          title="Programas de Produção"
          onChange={(e) => e.target.value && onAbrirProducao(e.target.value)}
        >
          <option value="">Produção ▾</option>
          <option value="consulta-producao">Consulta de Produção</option>
          <option value="fila">Fila de Produção</option>
          <option value="finalizacao-manual">Finalização Manual de PCF</option>
          <option value="reverter-cancelamento">Reverter Cancelamento de PCF</option>
          <option value="altera-tempo-etapa">Alteração de Tempo das Etapas</option>
          <option value="reabre-pcf">Reabertura de PCF</option>
          <optgroup label="Estoque">
            <option value="acerto-estoque">Acertos de Estoque / Corrida</option>
            <option value="historico-lote">Histórico de Lote</option>
            <option value="historico-pcf">Histórico de PCF</option>
          </optgroup>
        </select>
        <button
          className="btn-secundario"
          onClick={toggle}
          title={theme === "light" ? "Modo escuro" : "Modo claro"}
        >{theme === "light" ? "Modo escuro" : "Modo claro"}</button>
        <button className="btn-secundario" onClick={logout} title="Sair">Sair</button>
      </header>

      <BuscaPcf
        onAbrirPcf={(numpcf) => onAbrirConsulta("CONSULTA", cliente, numpcf)}
        onAbrirPedido={(numped) => onAbrirConsulta("PEDIDO", cliente, undefined, numped)}
      />

      <FiltrosBar
        clientes={clientes}
        clienteSelecionado={cliente}
        onClienteChange={setCliente}
        refreshSec={refreshSec}
        onRefreshSecChange={setRefreshSec}
        onRefreshNow={() => void carregar()}
      />

      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />

      {totais && (
        <div className="cards-topo">
          <CardTopo label="Pedidos não confirmados" valor={totais.pedidosNaoConfirmados} onClick={() => onAbrirPedidos(cliente)} />
          <CardTopo label="PCFs não programadas" valor={totais.pcfsNaoProgramadas} onClick={() => onAbrirNaoProgramadas(cliente)} />
          <CardTopo label="PCFs não iniciadas" valor={totais.pcfsNaoIniciadas} onClick={() => onAbrirConsulta("NAO_INICIADAS", cliente)} />
          <CardTopo label="PCFs paradas" valor={totais.pcfsParadas} onClick={() => onAbrirConsulta("PARADAS", cliente)} />
          <CardTopo
            label="PCFs atrasadas"
            valor={totais.pcfsAtrasadas}
            destaqueAtrasadas
            onClick={() => onAbrirConsulta("ATRASADAS", cliente)}
          />
        </div>
      )}

      {etapas && (
        <div className="etapas-section">
          <h2>Etapas</h2>
          <div className="etapas-grid">
            {etapas.map((et) => (
              <CardEtapa
                key={et.codigo}
                etapa={et}
                onClick={(e) => {
                  if (e.quantidade > 0) onAbrirEtapa(e, cliente);
                }}
              />
            ))}
          </div>
        </div>
      )}

      {!totais && !etapas && !erro && (
        <div className="status-loading">Carregando…</div>
      )}

    </div>
  );
}
